const assignmentKey = (person) => {
    if (!person) return "unassigned";
    return person.id != null
        ? `id:${person.id}`
        : `account:${person.account || ""}|name:${person.name || ""}`;
};

const summarizeAssignment = (tasks, field) => {
    const assignments = tasks.map((task) => task?.[field] || null);
    const keys = new Set(assignments.map(assignmentKey));

    if (keys.size <= 1) {
        return assignments[0];
    }

    return { name: "不一致", mixed: true };
};

const validTimestamp = (value) => {
    const timestamp = value ? Date.parse(value) : Number.NaN;
    return Number.isNaN(timestamp) ? null : timestamp;
};

const dateBoundary = (tasks, field, boundary) => {
    const candidates = tasks
        .map((task) => validTimestamp(task?.[field]))
        .filter((value) => value != null);

    if (candidates.length === 0) return null;
    return new Date(boundary(...candidates)).toISOString();
};

const uniqueTurbines = (tasks) => {
    const turbines = new Map();
    tasks.flatMap((task) => task?.turbines || []).forEach((turbine) => {
        const key = turbine?.id != null
            ? `id:${turbine.id}`
            : `seq:${turbine?.seq || ""}|name:${turbine?.name || ""}`;
        if (!turbines.has(key)) turbines.set(key, turbine);
    });
    return Array.from(turbines.values());
};

/**
 * Task rows remain turbine-scoped internally. The management page is session-scoped, so every
 * inspection session is represented by one summary row while retaining a representative task id
 * for the existing detail workflow.
 */
export const groupTasksBySession = (taskRows = []) => {
    const groups = new Map();

    taskRows.forEach((task, index) => {
        const sessionId = task?.sessionId ?? task?.session?.id;
        const key = sessionId != null ? `session:${sessionId}` : `task:${task?.id ?? index}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(task);
    });

    return Array.from(groups.values()).map((tasks) => {
        const representative = tasks[0] || {};
        const session = representative.session || null;
        const turbines = uniqueTurbines(tasks);

        if (!session) {
            return {
                ...representative,
                representativeTaskId: representative.id,
                taskCount: 1,
                totalTurbines: turbines.length,
                completedTurbines: 0,
            };
        }

        return {
            ...representative,
            id: representative.id,
            representativeTaskId: representative.id,
            sessionId: representative.sessionId ?? session.id,
            seq: session.seq || representative.seq,
            name: session.sessionName || representative.name,
            status: session.status || representative.status,
            session,
            owner_creater: summarizeAssignment(tasks, "owner_creater"),
            owner_checker: summarizeAssignment(tasks, "owner_checker"),
            owner_annotator: summarizeAssignment(tasks, "owner_annotator"),
            owner_rechecker: summarizeAssignment(tasks, "owner_rechecker"),
            created_at: dateBoundary(tasks, "created_at", Math.min) || representative.created_at,
            updated_at: dateBoundary(tasks, "updated_at", Math.max) || representative.updated_at,
            turbines,
            taskCount: tasks.length,
            totalTurbines: session.totalTurbines ?? turbines.length,
            completedTurbines: session.completedTurbines ?? 0,
        };
    });
};
